'use client';

import { useState } from 'react';
import {
  ApplicationEnvironmentEditDialog,
  ApplicationNameEditDialog,
  BusinessUnitsEditDialog,
  SettingsBooleanControl,
  SettingsCard,
  SettingsRow,
  SettingsSection,
  formatBusinessUnitNames,
  resolveApplicationEnvironment,
  toasterApi,
  useApplicationEnvironmentSelectCollection,
} from '@terra/ui-design-system';
import { VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import type { UpdateAiApplicationDto } from '@terra/shared-contracts-ai-applications';
import { AiApplicationInterfacesSettingsSection } from '@terra/ui-ai-applications';
import { useAiApplication } from '../../hooks/api/useAiApplication';
import { useUpdateAiApplication } from '../../hooks/api/useAiApplicationMutations';
import { useAiApplicationUrlEditRestriction } from '../../hooks/api/useAiApplicationUrlEditRestriction';
import { useProjectStore } from '../../store/projectStore';
import { useApplicationGeneralFields } from './target-settings/useApplicationGeneralFields';

type EditDialog = 'name' | 'environment' | 'businessUnits' | null;
type AiApplicationGeneralSettingsSectionProps = {
  applicationId: string;
  isReadOnly?: boolean;
};

export function AiApplicationGeneralSettingsSection({
  applicationId,
  isReadOnly,
}: AiApplicationGeneralSettingsSectionProps) {
  const tAi = useTranslations('aiApplications');
  const tAiSettings = useTranslations('aiApplications.settings');
  const tGeneral = useTranslations('designSystem.applicationGeneral');
  const tCommon = useTranslations('common');
  const tExecutions = useTranslations('designSystem.automation.executions');
  const selectedTarget = useProjectStore((state) => state.selectedTarget);
  const { aiApplication, isLoading } = useAiApplication({
    aiApplicationId: applicationId,
  });
  const { canEditUrl, urlEditRestrictionReason } =
    useAiApplicationUrlEditRestriction({
      aiApplicationId: applicationId,
    });
  const updateAiApplicationMutation = useUpdateAiApplication({
    aiApplicationId: applicationId,
  });
  const fields = useApplicationGeneralFields({ applicationId });
  const environmentCollection = useApplicationEnvironmentSelectCollection();
  const [editDialog, setEditDialog] = useState<EditDialog>(null);
  const isMcpServer = aiApplication?.kind === 'MCP_SERVER';
  const applicationName = selectedTarget?.name ?? '';
  const applicationEnvironment = resolveApplicationEnvironment(
    selectedTarget?.environment,
  );
  const isInternalApplication = selectedTarget?.isInternalApp ?? false;
  const closeDialog = (open: boolean) => {
    if (!open) {
      setEditDialog(null);
    }
  };

  const updateAiFields = async (dto: UpdateAiApplicationDto) => {
    try {
      await updateAiApplicationMutation.mutateAsync(dto);
      toasterApi.success({ title: tGeneral('saveSuccess') });

      return true;
    } catch (error: unknown) {
      toasterApi.error({
        title: tGeneral('saveError'),
        description: error instanceof Error ? error.message : undefined,
      });

      return false;
    }
  };

  return (
    <VStack alignItems="stretch" gap={8} w="full">
      <SettingsSection title={tGeneral('metadata')}>
        <SettingsCard isLoading={isLoading}>
          <SettingsRow
            label={tGeneral('name')}
            value={applicationName || tAi('emptyValue')}
            action={{
              label: tCommon('edit'),
              onClick: () => setEditDialog('name'),
              disabled: fields.isSavingFields,
            }}
            isReadOnly={isReadOnly}
          />
          {!isMcpServer ? (
            <SettingsRow
              label={tGeneral('url')}
              value={selectedTarget?.url || tAi('emptyValue')}
              valueWrap
              tooltip={tGeneral('urlDisabledTooltip')}
            />
          ) : null}
          <SettingsRow
            label={tGeneral('businessUnit')}
            value={formatBusinessUnitNames(fields.assignedBusinessUnits) ?? '-'}
            action={{
              label: tCommon('edit'),
              onClick: () => setEditDialog('businessUnits'),
              disabled: fields.isSavingBusinessUnits,
            }}
            isReadOnly={isReadOnly}
          />
          <SettingsRow
            label={tGeneral('environment')}
            value={
              environmentCollection.items.find(
                (item) => item.value === applicationEnvironment,
              )?.label ?? applicationEnvironment
            }
            action={{
              label: tCommon('edit'),
              onClick: () => setEditDialog('environment'),
              disabled: fields.isSavingFields,
            }}
            isReadOnly={isReadOnly}
          />
          <SettingsRow
            label={tAi('fields.kind')}
            value={isMcpServer ? tAi('kind.mcpServer') : tAi('kind.agent')}
          />
          <SettingsRow
            label={tAiSettings('general.enabled')}
            control={
              <SettingsBooleanControl
                checked={aiApplication?.isEnabled ?? true}
                label={
                  (aiApplication?.isEnabled ?? true)
                    ? tCommon('enabled')
                    : tCommon('disabled')
                }
                disabled={
                  isReadOnly ||
                  isLoading ||
                  updateAiApplicationMutation.isPending
                }
                onCheckedChange={({ checked }) => {
                  void updateAiFields({ isEnabled: checked });
                }}
              />
            }
          />
          <SettingsRow
            label={tExecutions('runAutomated.label')}
            tooltip={tExecutions('runAutomated.tooltip')}
            control={
              <SettingsBooleanControl
                checked={aiApplication?.smokeTestEnabled ?? false}
                label={
                  (aiApplication?.smokeTestEnabled ?? false)
                    ? tExecutions('yes')
                    : tExecutions('no')
                }
                disabled={
                  isReadOnly ||
                  isLoading ||
                  updateAiApplicationMutation.isPending
                }
                onCheckedChange={({ checked }) => {
                  void updateAiFields({ smokeTestEnabled: checked });
                }}
              />
            }
          />
          <SettingsRow
            label={tAiSettings('general.internalApplication')}
            control={
              <SettingsBooleanControl
                checked={isInternalApplication}
                label={isInternalApplication ? tCommon('yes') : tCommon('no')}
                disabled={isReadOnly || fields.isSavingFields}
                onCheckedChange={({ checked }) => {
                  void fields.saveFields({ isInternalApp: checked });
                }}
              />
            }
          />
        </SettingsCard>
      </SettingsSection>

      {!isMcpServer ? (
        <AiApplicationInterfacesSettingsSection
          aiApplication={aiApplication}
          isLoading={isLoading}
          isSaving={updateAiApplicationMutation.isPending}
          isReadOnly={isReadOnly}
          canEditUrl={canEditUrl}
          urlEditRestrictionReason={urlEditRestrictionReason}
          onSave={updateAiFields}
        />
      ) : null}

      <ApplicationNameEditDialog
        open={editDialog === 'name'}
        name={applicationName}
        loading={fields.isSavingFields}
        onOpenChange={closeDialog}
        onSubmit={(name) => fields.saveFields({ name })}
      />
      <ApplicationEnvironmentEditDialog
        open={editDialog === 'environment'}
        environment={applicationEnvironment}
        loading={fields.isSavingFields}
        onOpenChange={closeDialog}
        onSubmit={(environment) => fields.saveFields({ environment })}
      />
      <BusinessUnitsEditDialog
        open={editDialog === 'businessUnits'}
        businessUnits={fields.assignedBusinessUnits}
        allBusinessUnits={fields.allBusinessUnits}
        loading={fields.isSavingBusinessUnits}
        disabled={isReadOnly}
        isCreatingBusinessUnit={fields.isCreatingBusinessUnit}
        onOpenChange={closeDialog}
        onCreateBusinessUnit={fields.createBusinessUnitOption}
        onSubmit={fields.saveBusinessUnits}
      />
    </VStack>
  );
}
